'use client';

import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useClerk, useSession } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { FullPageLoader } from '@/components/shared/FullPageLoader';
import { isSessionSignOutMessage, SESSION_SIGN_OUT_EVENT, sessionTransition } from '@/lib/auth/session-transition';
import { AuthHeader } from './AuthHeader';
import { AuthSurface } from './AuthSurface';
import { authErrorClassName, authLinkClassName, authPrimaryButtonClassName } from './auth-presentation.consts';

export const SessionBoundary = ({ children }: { children: ReactNode }) => {
  const clerk = useClerk();
  const pathname = usePathname();
  const previousPath = useRef(pathname);
  const { isLoaded, session } = useSession();
  const transition = useSyncExternalStore(sessionTransition.subscribe, sessionTransition.getSnapshot, sessionTransition.getServerSnapshot);
  const identity = session ? `${session.user.id}:${session.id}` : null;
  const initial = useRef<{ identity: string | null } | null>(null);
  const currentSession = useRef(session?.id);
  currentSession.current = session?.id;
  const [now, setNow] = useState(0);

  if (isLoaded && initial.current === null) {
    initial.current = { identity };
  }
  const changed = isLoaded && initial.current !== null && initial.current.identity !== identity;

  useEffect(() => {
    if (previousPath.current !== pathname) {
      previousPath.current = pathname;
      // Browser Back/soft navigation may abandon SSO before its callback runs.
      // An active sign-out is deliberately unaffected by this cancellation.
      sessionTransition.cancelExpectedNavigation();
    }
  }, [pathname]);

  useEffect(() => {
    if (changed && !transition.navigationExpected) {
      sessionTransition.navigate(session ? '/account/' : '/');
    }
  }, [changed, session, transition.navigationExpected]);

  useEffect(() => {
    const receive = (message: unknown) => {
      if (isSessionSignOutMessage(message, currentSession.current) && !sessionTransition.getSnapshot().navigationExpected) {
        sessionTransition.navigate('/');
      }
    };
    let channel: BroadcastChannel | undefined;
    try {
      channel = new BroadcastChannel(SESSION_SIGN_OUT_EVENT);
      channel.onmessage = (event) => { receive(event.data); };
    } catch {
      // Storage and Clerk updates cover browsers without BroadcastChannel.
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === SESSION_SIGN_OUT_EVENT && event.newValue) {
        try { receive(JSON.parse(event.newValue)); } catch { /* Ignore unrelated malformed hints. */ }
      }
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Discard restored RSC/UI state and acquire current cookies on a new document request.
        sessionTransition.navigate(window.location.href);
      }
    };
    const onFocus = () => {
      const active = clerk.session;
      const key = active ? `${active.user.id}:${active.id}` : null;
      if (initial.current && key !== initial.current.identity && !sessionTransition.getSnapshot().navigationExpected) {
        sessionTransition.navigate(active ? '/account/' : '/');
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    return () => {
      channel?.close();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
    };
  }, [clerk]);

  useEffect(() => {
    if (transition.phase !== 'error') {
      return;
    }
    setNow(Date.now());
    const timer = window.setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { window.clearInterval(timer); };
  }, [transition.phase, transition.retryAt]);

  if (changed || transition.phase !== 'idle') {
    if (transition.phase !== 'error') {
      return <main><FullPageLoader label="Updating your session…" /></main>;
    }

    const seconds = Math.max(0, Math.ceil((transition.retryAt - now) / 1000));
    return <main className="min-h-dvh bg-[var(--ra-light-bg)]">
      <AuthSurface label="Session transition">
        <div className="space-y-7">
          <AuthHeader title="Finish signing out" />
          <p role="alert" className={authErrorClassName}><span>{transition.message}</span></p>
          <Button type="button" className={authPrimaryButtonClassName} disabled={seconds > 0} onClick={() => { void sessionTransition.retry(); }}>
            <span>{seconds > 0 ? `Retry in ${seconds}s` : 'Retry sign-out'}</span>
          </Button>
          {transition.canRecheck && <div className="space-y-3 border-t border-gray-100 pt-5 text-center">
            <p className="text-sm leading-6 text-gray-600"><span>If your session can no longer be verified, reload to check its current state. This does not confirm sign-out.</span></p>
            <Button type="button" variant="link" className={authLinkClassName} onClick={() => { sessionTransition.recheck(); }}><span>Reload session</span></Button>
          </div>}
        </div>
      </AuthSurface>
    </main>;
  }
  return children;
};
